import { useCallback, useSyncExternalStore } from "react";
import type { AppIcon, AppIconPreference } from "./library-app-icons";
import { isAppIcon } from "./library-app-icons";

export type LibraryPin = {
  id: string;
  name: string;
  mimeType: string;
  spaceId: string;
  isApp?: boolean;
};
type PinState = { ids: Set<string>; items: LibraryPin[]; icons: Record<string, AppIconPreference> };
const empty: PinState = { ids: new Set(), items: [], icons: {} };
const states = new Map<string, PinState>();
const listeners = new Set<() => void>();

export function libraryPinKey(userId: string): string {
  return `wurkees:library-pins:${userId}`;
}
const detailsKey = (userId: string) => `${libraryPinKey(userId)}:details`;
const iconsKey = (userId: string) => `${libraryPinKey(userId)}:icons`;

export function readLibraryPins(userId: string | null): Set<string> {
  if (!userId) return new Set();
  try {
    const value: unknown = JSON.parse(localStorage.getItem(libraryPinKey(userId)) ?? "[]");
    return new Set(
      Array.isArray(value)
        ? value.filter((id): id is string => validString(id, 200)).slice(0, 1000)
        : [],
    );
  } catch {
    return new Set();
  }
}

function validString(value: unknown, max: number): value is string {
  return typeof value === "string" && value.length > 0 && value.length <= max;
}
function validPin(value: unknown): value is LibraryPin {
  if (!value || typeof value !== "object") return false;
  const pin = value as LibraryPin;
  return (
    validString(pin.id, 200) &&
    validString(pin.spaceId, 200) &&
    validString(pin.name, 1000) &&
    validString(pin.mimeType, 200) &&
    (pin.isApp === undefined || typeof pin.isApp === "boolean")
  );
}
function pinDetails(item: LibraryPin): LibraryPin {
  return {
    id: item.id,
    name: item.name,
    mimeType: item.mimeType,
    spaceId: item.spaceId,
    ...(item.isApp ? { isApp: true } : {}),
  };
}
function snapshot(userId: string | null): PinState {
  if (!userId) return empty;
  const existing = states.get(userId);
  if (existing) return existing;
  const ids = readLibraryPins(userId);
  let items: LibraryPin[] = [];
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(detailsKey(userId)) ?? "[]");
    if (Array.isArray(raw)) {
      const unique = new Map(
        raw
          .filter(validPin)
          .filter((item) => ids.has(item.id))
          .slice(0, 1000)
          .map((item) => [item.id, pinDetails(item)]),
      );
      items = [...unique.values()];
    }
  } catch {
    /* Existing ID-only pins remain valid. */
  }
  const icons: Record<string, AppIconPreference> = {};
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(iconsKey(userId)) ?? "{}");
    if (raw && typeof raw === "object" && !Array.isArray(raw))
      for (const [id, entry] of Object.entries(raw).slice(0, 1000)) {
        const preference = entry as AppIconPreference;
        if (
          validString(id, 200) &&
          preference &&
          isAppIcon(preference.value) &&
          (preference.source === "app" || preference.source === "user")
        )
          Object.defineProperty(icons, id, {
            value: preference,
            enumerable: true,
            configurable: true,
            writable: true,
          });
      }
  } catch {
    /* Invalid artwork preferences do not affect saved pins. */
  }
  const state = { ids, items, icons };
  states.set(userId, state);
  return state;
}
function publish(userId: string, state: PinState) {
  states.set(userId, state);
  try {
    localStorage.setItem(libraryPinKey(userId), JSON.stringify([...state.ids]));
    localStorage.setItem(detailsKey(userId), JSON.stringify(state.items));
    localStorage.setItem(iconsKey(userId), JSON.stringify(state.icons));
  } catch {
    /* The shared in-memory preference still works if browser storage is unavailable. */
  }
  for (const listener of listeners) listener();
}
function storageChanged(event: StorageEvent) {
  if (event.key !== null && !event.key.startsWith("wurkees:library-pins:")) return;
  states.clear();
  for (const listener of listeners) listener();
}
function subscribe(listener: () => void) {
  if (!listeners.size) {
    // Read fresh browser preferences when returning after all consumers unmounted.
    states.clear();
    window.addEventListener("storage", storageChanged);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (!listeners.size) window.removeEventListener("storage", storageChanged);
  };
}

/** Per-user browser preferences, synchronized between Library, the rail and open tabs. */
export function useLibraryPins(userId: string | null) {
  const state = useSyncExternalStore(
    subscribe,
    () => snapshot(userId),
    () => empty,
  );
  const toggle = useCallback(
    (id: string, item?: LibraryPin) => {
      if (!userId || !validString(id, 200)) return;
      const current = snapshot(userId);
      const ids = new Set(current.ids);
      if (ids.has(id)) ids.delete(id);
      else if (ids.size < 1000) ids.add(id);
      const details = new Map(current.items.map((pin) => [pin.id, pin]));
      if (item?.id === id && validPin(item) && ids.has(id)) details.set(id, pinDetails(item));
      publish(userId, {
        ...current,
        ids,
        items: [...details.values()].filter((pin) => ids.has(pin.id)),
      });
    },
    [userId],
  );
  const remember = useCallback(
    (items: readonly LibraryPin[]) => {
      if (!userId) return;
      const current = snapshot(userId);
      const details = new Map(current.items.map((pin) => [pin.id, pin]));
      let changed = false;
      for (const item of items) {
        if (!current.ids.has(item.id) || !validPin(item)) continue;
        const pin = pinDetails(item);
        if (JSON.stringify(details.get(item.id)) === JSON.stringify(pin)) continue;
        details.set(item.id, pin);
        changed = true;
      }
      if (changed) publish(userId, { ...current, items: [...details.values()] });
    },
    [userId],
  );
  const remove = useCallback(
    (id: string) => {
      if (userId && snapshot(userId).ids.has(id)) toggle(id);
    },
    [userId, toggle],
  );
  const setAppIcon = useCallback(
    (id: string, value: AppIcon, source: "app" | "user" = "user") => {
      if (!userId || !validString(id, 200) || !isAppIcon(value)) return;
      const current = snapshot(userId);
      const existing = current.icons[id];
      if (source === "app" && existing?.source === "user") return;
      const preference = { value, source };
      if (JSON.stringify(existing) === JSON.stringify(preference)) return;
      if (!existing && Object.keys(current.icons).length >= 1000) return;
      publish(userId, { ...current, icons: { ...current.icons, [id]: preference } });
    },
    [userId],
  );
  return { ...state, toggle, remember, remove, setAppIcon };
}
